/**
 * The routes of the light questioning in `router.tsx` (UC-8): the recorder of one subject —
 * `cases/:caseId/humint/subjects/:subjectRef/recording` —, the recorder of one encounter, which
 * asks who takes part — `cases/:caseId/humint/encounters/:encounterRef/recording` (ADR-0057) —,
 * the page of one recording — `cases/:caseId/humint/recordings/:captureId` — and the case's
 * recordings, the section *Výsluchy* — `cases/:caseId/humint/sessions`. A subject ref is a graph
 * element id (`4:…:37`), so each segment is encoded. The page of a recording opens at a time
 * with `?t=<whole seconds>`: a search hit, a passage on the graph and a citation lead to the
 * moment they name.
 */
import { subjectPath } from '../subject-path'

export function recorderPath(caseId: string, subjectRef: string): string {
  return `${subjectPath(caseId, subjectRef)}/recording`
}

export function encounterRecorderPath(caseId: string, encounterRef: string): string {
  return `/cases/${caseId}/humint/encounters/${encodeURIComponent(encounterRef)}/recording`
}

/** The search param of the recording page that holds the time to open at, in whole seconds. */
export const RECORDING_TIME_PARAM = 't'

/** `atSeconds`: the time the page opens at; left out, or anything that is no time, opens it plain. */
export function recordingPath(
  caseId: string,
  captureId: string,
  atSeconds?: number | null
): string {
  const path = `/cases/${caseId}/humint/recordings/${encodeURIComponent(captureId)}`
  const timed = typeof atSeconds === 'number' && Number.isFinite(atSeconds) && atSeconds >= 0
  return timed ? `${path}?${RECORDING_TIME_PARAM}=${Math.floor(atSeconds)}` : path
}

/** Where a passage is heard: the page of a HUMINT recording, or the document of any other. */
export type PassageSource =
  | { kind: 'recording'; to: string }
  | { kind: 'document'; documentId: string }

/** What a passage — a node of the graph, a search hit — says of where it lies. */
export interface PassagePlace {
  /** The HUMINT capture the passage's recording is; none for an audio file uploaded to the case. */
  captureId?: string | null
  documentId?: string | null
  /** Where the passage starts in its recording, in seconds. */
  atSeconds?: number | null
}

/**
 * How a passage opens its recording, decided once for everything that shows one. Only the
 * recording of a HUMINT capture has a page that takes a time: its passage opens that page at the
 * second it starts in. Any other recording opens as the case document it is; a passage that names
 * neither leads nowhere.
 */
export function passageSource(caseId: string, passage: PassagePlace): PassageSource | null {
  const { captureId, documentId, atSeconds } = passage
  if (captureId) return { kind: 'recording', to: recordingPath(caseId, captureId, atSeconds) }
  return documentId ? { kind: 'document', documentId } : null
}

/**
 * The time the address asks the recording page to open at, in milliseconds as the player counts;
 * null where it names none. A time survives the address only as plain digits, so a hand-edited
 * link cannot put a NaN, a fraction or a clock time into the player.
 */
export function recordingStartMs(search: URLSearchParams): number | null {
  const raw = search.get(RECORDING_TIME_PARAM)
  if (raw === null || !/^\d+$/.test(raw)) return null
  const ms = Number.parseInt(raw, 10) * 1000
  return Number.isSafeInteger(ms) ? ms : null
}

/**
 * The time to open at, held against the recording's length, both in one unit: a time at or past
 * its end is ignored, as any other value that is no time of this recording. `length` is 0 while
 * the length is not known; the time then stands. The one place that rule is written: the page
 * holds the address's time against the length the server measured, the player (`startSecondsFor`)
 * against the audio element's own.
 */
export function startInside(start: number | null, length: number): number | null {
  if (start === null) return null
  return length > 0 && start >= length ? null : start
}

/**
 * What the subject page hands the recorder in the navigation's state, never in the address: the
 * encounter the recording joins unless the operator picks another.
 */
export interface RecorderRouteState {
  /** The subject's current encounter (`enc-<uuid>`); null for none. */
  encounterRef: string | null
}

/**
 * The encounter the recorder was opened for. The state is whatever the history entry holds:
 * opened by its address, or from another page's state, the recorder knows none.
 */
export function recorderEncounter(state: unknown): string | null {
  const given = (state as Partial<Record<keyof RecorderRouteState, unknown>> | null)?.encounterRef
  return typeof given === 'string' ? given : null
}
